import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import BlockWithSortVisible from './BlockWithSortVisible';
import BlockWithVisible from './BlockWithVisible';
import { SortDirection, WorkSortOrder } from '../../shared/enum/EnumRepository';
import { useWorkListStore } from '../../shared/store/WorkListStore';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkIndexing } from '../../shared/hooks/useWorkIndexing';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import WorkOverviewEditActions from './WorkOverviewEditActions';


const ColumnInfo: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { workPropertyVisible, getWorkPropertyVisible } = useWorkPropertyVisible();
  const { requestExitEditMode } = useUnsavedChanges();
  const sortData = useWorkListStore((s) => s.sortData);
  const setSortData = useWorkListStore((s) => s.setSortData);
  const isOverviewOpen = useWorkViewStore((s) => s.isOverviewOpen);
  const { isIndexingMode } = useWorkIndexing();

  useEffect(() => {
    const getWorkPropertyVisibleWithApi = async () => {
      console.log("getting workPropertyVisible...");
      await getWorkPropertyVisible();
    }
    getWorkPropertyVisibleWithApi();
  }, []);

  const isOverviewOpenWithIndexing = isOverviewOpen || isIndexingMode;

  if (!workPropertyVisible) return null;

  const handleDirectionChange = (field: WorkSortOrder) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }

    // 현재 정렬 필드와 같으면 방향만 변경, 다르면 해당 필드로 정렬하고 ASC로 설정
    if (sortData.sort === field) {
      setSortData({
        sort: field,
        direction: sortData.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      });
    } else {
      setSortData({ sort: field, direction: SortDirection.ASC });
    }
  };

  return (
    <ColumnInfoComp>
      <InfoContainer>
        <ImageBlock />
        <TitleBlock onClick={() => handleDirectionChange(WorkSortOrder.TITLE)}>
          Title
          <SelectDirection
            $isASC={sortData.sort === WorkSortOrder.TITLE && sortData.direction === SortDirection.ASC}
            $isSortField={sortData.sort === WorkSortOrder.TITLE}
          >
            ▼
          </SelectDirection>
        </TitleBlock>
        <BlockWithVisible
          width={"1.5"}
          isColumn={true}
          value={"Size(unit)"}
        />
        <BlockWithVisible
          width={"2"}
          isColumn={true}
          value={"Material"}
        />
        <BlockWithSortVisible
          width={"0.8"}
          isColumn={true}
          field={WorkSortOrder.YEAR}
          value={"Year"}
        />
        <BlockWithVisible
          width={"1.2"}
          isColumn={true}
          field={"workType"}
          value={"Type"}
        />
        <BlockWithVisible
          width={"1"}
          isColumn={true}
          field={"price"}
          value={"Price"}
        />
        <BlockWithVisible
          width={"1"}
          isColumn={true}
          field={'collection'}
          value={"Collection"}
        />
        {isOverviewOpenWithIndexing && <SpaceBlock />}
      </InfoContainer>
      {isOverviewOpenWithIndexing && (
        <OverviewBlock>
          {isIndexingMode ? 'Indexing' : 'Overview'}
          <OverviewActions id="work-overview-actions">
            <WorkOverviewEditActions />
          </OverviewActions>
        </OverviewBlock>
      )}
    </ColumnInfoComp>
  )
}

const ColumnInfoComp = styled.article`
  width: 100%;
  height: 38px;
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_02};

  ${({ theme }) => theme.typography.Body_03_2};
`;

const InfoContainer = styled.article`
  flex: 1;
  width: auto;
  height: 34px;
  display: flex;
`;

const ImageBlock = styled.div`
  flex: 0.5;
  min-width: 40px;
  max-width: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const TitleBlock = styled.div`
  flex: 2.5;
  display: flex;
  align-items: center;
  padding-left: 10px;
  cursor: pointer;
  user-select: none;
`
const SelectDirection = styled.span<{ $isSortField: boolean, $isASC: boolean }>`
  margin-left: 4px;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme, $isSortField }) => $isSortField ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  transform: ${({ $isASC, $isSortField }) => {
    if (!$isSortField) return "scale(0.7)"; // 기본: dim 처리된 ▼
    return $isASC ? "scale(0.7)" : "scaleY(-1) scale(0.7)"; // ASC면 그대로, DESC면 뒤집기
  }};
  transition: transform 0.6s ease-in-out;
`;

const SpaceBlock = styled.div`
  flex: 0.15;
  
  display: flex;
  align-items: center;
  justify-content: center;
`

const OverviewBlock = styled.div`
  width: 29vw;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 2px;
`

const OverviewActions = styled.div`
  display: flex;
  margin-left: auto;
`;


export default ColumnInfo;
